import React, { useEffect, useState } from "react";
import "./jwplayer-resume.m.css";
import { currentUser } from "@quintype/bridgekeeper-js";
import { getAStory } from "../../../../../../api/utils";
import { getStoryBySection } from "../../../../../../api/utils";

const JWplayerIndividualVideo = ({ uniqueId, sendDataToParent, videoObject, storyAttributes, storyObj={} }) => {
  const playerId = uniqueId;
  const [videoCompleted, setVideoCompleted] = useState(false);
  const { singleVideoFile, libraryId } = videoObject;
  const [lastPosition, setLastPosition] = useState(0);
  const [userId, setUserId] = useState(null);
  const apiPostUrl = `/user-history/addOrUpdate`;

  useEffect(() => {
    const fetchUserId = async() => {
      const currentUserResp = await currentUser();
      const userId = currentUserResp?.user?.id || null;
      setUserId(userId);
    };
    fetchUserId();
  }, []);

  const handlePostRequest = async (storyID, position) => {
    const currentUserResp = await currentUser();
    const userId = currentUserResp?.user?.id || null;
    if (!userId) return;

    // prepare the metadata field
    let objMetadata={};
    objMetadata["slug"] = storyObj?.slug || "";
    objMetadata["headline"] = storyObj?.headline || "";
    objMetadata["hero-image-s3-key"] = storyObj?.["hero-image-s3-key"] || "";
    objMetadata["hero-image-metadata"] = storyObj?.["hero-image-metadata"] || {};
    objMetadata["story-type"] = storyObj?.metadata?.["story-attributes"]?.storytype?.[0] || "";

    const data = { user_id: userId, media_id: storyID, last_position_media_time: position, metadata: objMetadata };
    try {
      const response = await fetch(apiPostUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (response.ok) {
        console.log("Successfully posted data");
      } else {
        console.error("Failed to post data");
      }
    } catch (error) {
      console.error("some error:", error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      const apiUrl = `/user-history/getByMediaId`;
      try {
        const data = { user_id: userId, media_id: uniqueId };
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        });
        if (response.ok) {
          const data = await response.json();
          if (data?.data?.last_position_media_time) {
            const lastPos = `${data?.data?.last_position_media_time}`
            setLastPosition(lastPos);
          }
        } else {
          console.error("Failed to fetch data");
        }
      } catch (err) {
        console.log("ERROR WHILE FETCHING USER HISTORY",err)
        console.error("Error fetching data:", err);
      }
    };

    fetchData();
  }, [uniqueId,userId]);

  // Function to handle player events
  const setupJWPlayerEvents = (player, isMounted) => {
    player.on("pause", () => {
      if (isMounted) {
        const currentTime = player.getPosition();
        handlePostRequest(uniqueId, currentTime);
      }
    });
  // Handle manual seeking event
  player.on("seek", (event) => {
    const newTime = event.offset;
    handlePostRequest(uniqueId, newTime);
  });
    player.on("fullscreen", (event) => {
      //remove this if required to exit fullscreen to go back previous page.
    /*  if (isMounted && !event.fullscreen) {
        setVideoCompleted(true);
      }*/
    });

    player.on("complete", () => {
      // set the player "seek" to "0", so that during "history.back" event, correct time is recorded in db
      player.seek("0.0");

      const getNextStory = (currentStory, storyList) => {
        if (!currentStory || !storyList || storyList.length === 0) {
          return null;
        }

        // Sort the stories by published date in ascending order
        const sortedStories = [...storyList].sort((a, b) => new Date(a["published-at"]) - new Date(b["published-at"]));

        // Find the next story whose published date is just greater than the current story
        return sortedStories.find(story => new Date(story["published-at"]) > new Date(currentStory["published-at"])) || null;
      };

      if (isMounted) {
        setVideoCompleted(true);
        getAStory(uniqueId)
          .then((story) => {
            if (story?.sections?.length > 0) {
              const firstSectionId = story?.sections?.[0]?.id ?? null;

              if (firstSectionId) {
                getStoryBySection(firstSectionId)
                  .then((stories) => {
                    const nextStory = getNextStory(story, stories);
                    const nextStoryID = nextStory?.id ?? null;
                    if (nextStoryID) {
                      // add the next episode to the user history
                      handlePostRequest(nextStoryID, "0.1");
                    }
                  })
                  .catch((error) => {
                    console.error("Error fetching stories:", error);
                  });
              }
            }
          })
          .catch((error) => {
            console.error("Error fetching the story:", error);
          });

          // update the current video's timestamp to 0, as it's fully watched now
      }
    });
  };

  // Function to initialize JWPlayer
  const initializeJWPlayer = (isMounted) => {
    const player = window.jwplayer(playerId).setup({
      playlist: singleVideoFile,
      skin: {
        name: "seven",
        inactive: "#00fff",
        background: "rgba(64, 64, 64, 0.8)",
        tooltips: { background: "#FFFFFF", text: "#000000" },
        controlbar: { background: "rgba(64, 64, 64, 0.8)", icons: "rgba(255,255,255,0.8)", text: "#FFFFFF" },
      },
      allowFullscreen: true,
      autostart: true,
      //...(storyAttributes === "audio" && { width: "920", height: "52" }),
    });

    if (lastPosition && isMounted) {
      player.once("play", () => {
        player.seek(lastPosition); // Start from last position if exists
      });
    }

    setupJWPlayerEvents(player, isMounted);
  };

  // Effect to dynamically load JWPlayer script and initialize the player
  useEffect(() => {
    let isMounted = true;

    const loadJWPlayerScript = () => {
      const script = document.createElement("script");
      script.src = `https://cdn.jwplayer.com/libraries/${libraryId}.js`;
      script.async = true;
      script.onload = () => isMounted && initializeJWPlayer(isMounted);
      document.body.appendChild(script);
    };

    if (document.getElementById(playerId)) {
      if (!window.jwplayer) {
        loadJWPlayerScript();
      } else {
        initializeJWPlayer(isMounted);
      }
    } else {
      console.error("Player container is not yet mounted in the DOM");
    }

    // Save current time when the page is about to unload
    const handleBeforeUnload = () => {
      if (window.jwplayer && isMounted) {
        const currentTime = window.jwplayer(playerId).getPosition();
        handlePostRequest(uniqueId, currentTime);
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup JWPlayer instance and event listeners on component unmount
    return () => {
      isMounted = false;
      if (window.jwplayer) {
        window.jwplayer(playerId).remove();
      }
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [singleVideoFile, libraryId, playerId, lastPosition]);

  // Effect to notify parent when video completes
  useEffect(() => {
    if (videoCompleted) {
      sendDataToParent(true);
    }
  }, [videoCompleted, sendDataToParent]);

  useEffect(() => {
    const handleWrapperPostRequest  = () => {
      if (window.jwplayer && playerId) {
        const currentTime = window.jwplayer(playerId).getPosition();
        handlePostRequest(uniqueId, !currentTime ? "0.0" : currentTime);
      }
    };

    const handleNavigationClick = (event) => {
      const target = event.target.closest("a, .ri-arrow-left-line");

      if (target) {
        handleWrapperPostRequest();
      }
    };

    const handleBackButton = () => {
      handleWrapperPostRequest();
    };

    document.addEventListener("click", handleNavigationClick);
    window.addEventListener("popstate", handleBackButton);

    return () => {
      document.removeEventListener("click", handleNavigationClick);
      window.removeEventListener("popstate", handleBackButton);
    };
  }, []);

  return (
    <div styleName="myskin">
      <div id={playerId}></div>
    </div>
  );
};

export default JWplayerIndividualVideo;
